import type {
  AnomalyFlag,
  DecisionItem,
  Kpi,
  ReconciliationRow,
  SourceFile,
} from './types'

export const RAW_VARIANCE = 963_700.05
export const RECONCILED_BALANCE = 11_788_989.84

export const closeMeta = {
  entity: 'NovaByte Analytics, Inc.',
  account: 'Pacific Commerce Bank - 10100',
  period: 'April 30, 2025',
  controller: 'Marlowe Mitchell',
  staffAccountant: 'Beckett Roberts',
  agent: 'EC-07 CrossCore AI',
  runAt: '2025-05-01 00:12 EST',
}

export const workflowSteps = [
  'GL Load',
  'Bank Load',
  'Matching',
  'Variance Analysis',
  'HITL Decisions',
  'JE Generation',
  'NetSuite Upload',
  'Close Signoff',
]

export const kpis: Kpi[] = [
  { label: 'Opening Balance', value: '$8,377,018' },
  { label: 'Total Deposits', value: '$12,359,905' },
  { label: 'Total Disbursements', value: '$8,947,933' },
  { label: 'Bank Closing', value: '$11,788,990' },
  { label: 'GL Cash Balance', value: '$12,752,690' },
]

export const bankRows: ReconciliationRow[] = [
  { label: 'Bank statement closing balance', amount: 11_788_989.84 },
  { label: '+ Deposits in transit', amount: 0, indent: true },
  { label: '- Outstanding checks / ACH', amount: 0, indent: true },
]

export const bookRows: ReconciliationRow[] = [
  { label: 'GL opening balance (Apr 1)', amount: 8_377_017.6 },
  { label: '+ Period net activity (10100)', amount: 4_375_672.29, indent: true },
  { label: '= GL closing balance', amount: 12_752_689.89, indent: true },
  {
    label: '- Missing vendor ACH not in GL',
    amount: -6_250_176.68,
    tone: 'negative',
    indent: true,
  },
  {
    label: '+ Customer deposits not yet in GL',
    amount: 6_168_072.43,
    tone: 'positive',
    indent: true,
  },
  {
    label: '- Payroll wires on bank / GL in 10130',
    amount: -843_916.66,
    tone: 'negative',
    indent: true,
  },
  {
    label: '- Interest JE overstatement',
    amount: -46_920.74,
    tone: 'negative',
    indent: true,
  },
  {
    label: '+ Fee JE overstatement',
    amount: 9_241.6,
    tone: 'positive',
    indent: true,
  },
]

export const decisionItems: DecisionItem[] = [
  {
    id: 1,
    code: 'JE-EC07-001',
    priority: 'HIGH',
    title: 'Missing Vendor ACH Payments - Not Posted to GL (10100)',
    summary:
      'Bank ACH disbursements exceed GL bill payments on 10100 by $6,250,176.68.',
    detail:
      'AP Aging confirms 15 additional vendor payments cleared the bank in April but were not posted to account 10100 in the current GL extract. Approving posts one correcting JE to credit 10100 and debit AP Clearing.',
    amount: 6_250_176.68,
    amountLabel: 'GL credit to 10100',
    impactDirection: -1,
    amountEditable: true,
    notePlaceholder: 'Controller note required before approval',
    approveLabel: 'Approve',
    modifyLabel: 'Modify amount',
    rejectLabel: 'Reject',
    jeHeader: 'Proposed journal entry',
    jeLines: [
      {
        entry: '1',
        account: '20100 - AP',
        description: 'AP Clearing - vendor payments Apr',
        debit: 6_250_176.68,
      },
      {
        entry: '2',
        account: '10100 - Cash',
        description: 'Vendor ACH disbursements Apr',
        credit: 6_250_176.68,
      },
    ],
    memo: 'EC-07 Apr 2025 - Post missing vendor ACH payments to primary checking.',
    jeType: 'Missing Vendor ACH Payments',
    sourceDocuments: 'Bank stmt ACH debits vs GL PAY44001-PAY44006',
  },
  {
    id: 2,
    code: 'JE-EC07-002',
    priority: 'HIGH',
    title: 'Missing Customer Deposits - Bank Wire-Ins Not Fully Posted to GL',
    summary:
      'Bank received $12,354,799 in customer wires. GL records only $6,186,726.',
    detail:
      'AR confirms all 31 bank wire-ins have been matched to customer invoices. The delta represents received cash where GL cash application has not posted. Approving debits 10100 and credits AR Clearing.',
    amount: 6_168_072.43,
    amountLabel: 'GL debit to 10100',
    impactDirection: 1,
    amountEditable: true,
    notePlaceholder: 'Controller note required before approval',
    approveLabel: 'Approve',
    modifyLabel: 'Modify amount',
    rejectLabel: 'Reject',
    jeHeader: 'Proposed journal entry',
    jeLines: [
      {
        entry: '1',
        account: '10100 - Cash',
        description: 'Customer wire receipts Apr - AR clearing',
        debit: 6_168_072.43,
      },
      {
        entry: '2',
        account: '12100 - AR',
        description: 'AR application - customer wires Apr',
        credit: 6_168_072.43,
      },
    ],
    memo: 'EC-07 Apr 2025 - Post customer wire receipts not yet applied in GL.',
    jeType: 'Missing Customer Wire Receipts',
    sourceDocuments: 'Bank wires DEP954815-DEP613426 vs GL DEP50401-DEP50487',
  },
  {
    id: 3,
    code: 'JE-EC07-003',
    priority: 'HIGH',
    title: 'Payroll Funding Transfer - Missing Inter-Account JE',
    summary:
      'Two payroll wires clear primary bank 10100; GL routes payroll through 10130.',
    detail:
      'NovaByte funds payroll checking from primary checking. The funding transfers hit the bank on Apr 15 and Apr 30, but no 10100 to 10130 funding transfer JE was found.',
    amount: 843_916.66,
    amountLabel: '2 x $421,958.33',
    impactDirection: -1,
    amountEditable: false,
    notePlaceholder: 'Controller note required before approval',
    approveLabel: 'Approve',
    rejectLabel: 'Reject',
    jeHeader: 'Proposed journal entries',
    jeLines: [
      {
        entry: '003A',
        account: '10130 - Payroll',
        description: 'Payroll funding transfer 4/15',
        debit: 421_958.33,
      },
      {
        entry: '003A',
        account: '10100 - Cash',
        description: 'Payroll funding transfer 4/15',
        credit: 421_958.33,
      },
      {
        entry: '003B',
        account: '10130 - Payroll',
        description: 'Payroll funding transfer 4/30',
        debit: 421_958.33,
      },
      {
        entry: '003B',
        account: '10100 - Cash',
        description: 'Payroll funding transfer 4/30',
        credit: 421_958.33,
      },
    ],
    memo: 'EC-07 Apr 2025 - Inter-account payroll funding transfer 10100 to 10130.',
    jeType: 'Payroll Funding Transfer',
    sourceDocuments: 'Bank PAYROLL8529 and PAYROLL1266 vs GL JE30403 and JE30407',
  },
  {
    id: 4,
    code: 'JE-EC07-004',
    priority: 'MEDIUM',
    title: 'Interest JE Overstated - JE30426 vs Bank INT26284',
    summary:
      'GL debits 10100 for $52,027.60 in interest; bank shows only $5,106.86.',
    detail:
      'JE30426 likely combines checking interest with investment income from other accounts. Recommended action is to reverse the $46,920.74 excess and reclassify to the appropriate investment income account.',
    amount: 46_920.74,
    amountLabel: 'GL overstatement',
    impactDirection: -1,
    amountEditable: true,
    notePlaceholder: 'Confirm source of excess interest',
    approveLabel: 'Approve correction',
    modifyLabel: 'Modify amount',
    rejectLabel: 'Carry as timing item',
    jeHeader: 'Proposed correcting entry',
    jeLines: [
      {
        entry: '1',
        account: '11020 - Invest.',
        description: 'Reverse excess interest from JE30426',
        debit: 46_920.74,
      },
      {
        entry: '2',
        account: '10100 - Cash',
        description: 'Correct interest overposted to checking',
        credit: 46_920.74,
      },
    ],
    memo: 'EC-07 Apr 2025 - Partial reversal JE30426 interest overposted to 10100.',
    jeType: 'Interest JE Correction',
    sourceDocuments: 'Bank INT26284 $5,106.86 vs GL JE30426 $52,027.60',
  },
  {
    id: 5,
    code: 'JE-EC07-005',
    priority: 'MEDIUM',
    title: 'Fee JE Overstated - JE30427 vs Bank FEE3588',
    summary:
      'GL credits 10100 for $10,413.37 in fees; bank shows $1,171.77.',
    detail:
      'JE30427 may be an accrual for expected fees or an entry covering other Pacific Commerce accounts. If erroneous, reverse the $9,241.60 excess. If valid timing, reject and document carry-forward.',
    amount: 9_241.6,
    amountLabel: 'GL overstatement',
    impactDirection: 1,
    amountEditable: true,
    notePlaceholder: 'Accrual or error? Provide source',
    approveLabel: 'Reverse error',
    modifyLabel: 'Modify amount',
    rejectLabel: 'Carry as timing item',
    jeHeader: 'Proposed correcting entry',
    jeLines: [
      {
        entry: '1',
        account: '10100 - Cash',
        description: 'Reverse excess fee - JE30427 correction',
        debit: 9_241.6,
      },
      {
        entry: '2',
        account: '69000 - Admin',
        description: 'Reverse fee expense overstatement',
        credit: 9_241.6,
      },
    ],
    memo: 'EC-07 Apr 2025 - Partial reversal JE30427 fee overstatement on 10100.',
    jeType: 'Fee JE Correction',
    sourceDocuments: 'Bank FEE3588 $1,171.77 vs GL JE30427 $10,413.37',
  },
]

export const anomalyFlags: AnomalyFlag[] = [
  {
    severity: 'HIGH',
    transactionId: 'DEP223088',
    description: 'Wire In - Singapore Genome Institute',
    amount: 736_574.58,
    action: 'Largest single deposit. Confirm signed contract or PO.',
  },
  {
    severity: 'HIGH',
    transactionId: 'ACH923856',
    description: 'ACH Out - Cumulus Cloud Services',
    amount: 654_871.1,
    action: 'Exceeds $500K threshold. Confirm against invoice.',
  },
  {
    severity: 'MED',
    transactionId: 'DEP718836',
    description: 'Wire In - Defense Research Agency',
    amount: 781_530.73,
    action: 'Confirm revenue recognition period and federal billing compliance.',
  },
  {
    severity: 'MED',
    transactionId: 'ACH815707',
    description: 'ACH Out - Various Vendors',
    amount: 237_024.24,
    action: 'Obtain itemized vendor schedule from AP before close signoff.',
  },
  {
    severity: 'LOW',
    transactionId: 'ACH204411',
    description: 'ACH Out - Various Vendors',
    amount: 37_676.25,
    action: 'Request AP detail schedule.',
  },
  {
    severity: 'INFO',
    transactionId: 'PAYROLL8529',
    description: 'Payroll Wire - PayrollPro Services',
    amount: 421_958.33,
    action: 'Expected bi-monthly payroll. Also subject to JE-EC07-003.',
  },
  {
    severity: 'INFO',
    transactionId: 'PAYROLL1266',
    description: 'Payroll Wire - PayrollPro Services',
    amount: 421_958.33,
    action: 'Second payroll run, identical amount. Expected.',
  },
]

export const sourceFiles: SourceFile[] = [
  {
    label: 'Bank statements',
    description: 'Original bank transaction workbook.',
    href: '/bank-reconciliation/04_Bank_Statements.xlsx',
  },
  {
    label: 'GL dataset',
    description: 'Original GL cash account workbook.',
    href: '/bank-reconciliation/01_GL_Dataset.xlsx',
  },
  {
    label: 'Original console',
    description: 'Standalone HITL HTML source.',
    href: '/bank-reconciliation/EC07_BankRec_Apr2025_Interactive.html',
  },
  {
    label: 'NetSuite JE sample',
    description: 'Reference CSV generated by the source package.',
    href: '/bank-reconciliation/EC07_JE_Apr2025_NetSuite.csv',
  },
  {
    label: 'Audit trail sample',
    description: 'Reference audit CSV generated by the source package.',
    href: '/bank-reconciliation/EC07_AuditTrail_Apr2025.csv',
  },
  {
    label: 'Agent skill',
    description: 'Bank reconciliation agent operating procedure.',
    href: '/bank-reconciliation/Bank_Reconciliation_Skill_UPDATED.md',
  },
  {
    label: 'Project instructions',
    description: 'EC-07 project instructions and handoff conditions.',
    href: '/bank-reconciliation/EC07_Project_Instructions_UPDATED.md',
  },
]
